import React, { cloneElement, isValidElement, JSX, PropsWithoutRef, ReactElement, useEffect, useRef } from "react";
import AnimateTransform from "./AnimateTransform";
import styles from "./IntegrationWheel.module.scss";
import WebGazerLogo from "./WebGazerLogo";
import {
  circleCenter,
  circleRadius,
  viewBox,
  wheelDotRadius,
  wheelHeight,
  wheelLogoRadius,
  wheelLogoStrokeWidth,
  wheelWidth,
} from "./constants";

export type ImageProps = Omit<PropsWithoutRef<JSX.IntrinsicElements["svg"]>, "children">;

const logos: Array<[string, JSX.Element]> = [
  ["chat", (
    <g key="chat">
      <circle
        cx={wheelLogoRadius}
        cy={wheelLogoRadius}
        fill="#3B82F6"
        r={wheelLogoRadius - wheelLogoStrokeWidth}
        stroke="white"
        strokeWidth={wheelLogoStrokeWidth}
      />

      <path
        d="M15.2935 44.7077V18.2371C15.2935 17.4283 15.5814 16.7358 16.1574 16.1599C16.7334 15.5839 17.4258 15.2959 18.2346 15.2959H41.764C42.5729 15.2959 43.2653 15.5839 43.8413 16.1599C44.4172 16.7358 44.7052 17.4283 44.7052 18.2371V35.8841C44.7052 36.693 44.4172 37.3854 43.8413 37.9613C43.2653 38.5373 42.5729 38.8253 41.764 38.8253H21.1758L15.2935 44.7077ZM21.1758 32.943H32.9405V30.0018H21.1758V32.943ZM21.1758 28.5312H38.8229V25.59H21.1758V28.5312ZM21.1758 24.1194H38.8229V21.1783H21.1758V24.1194Z"
        fill="white"
      />
    </g>
  )],
  ["call", (
    <g key="call">
      <circle
        cx={wheelLogoRadius}
        cy={wheelLogoRadius}
        fill="#84CC16"
        r={wheelLogoRadius - wheelLogoStrokeWidth}
        stroke="white"
        strokeWidth={wheelLogoStrokeWidth}
      />

      <path
        d="M42.861 44.5551C39.4909 44.5551 36.1613 43.8205 32.872 42.3511C29.5828 40.8817 26.5902 38.799 23.8941 36.1029C21.198 33.4069 19.1153 30.4142 17.6459 27.125C16.1766 23.8358 15.4419 20.5061 15.4419 17.136C15.4419 16.6507 15.6037 16.2463 15.9272 15.9228C16.2507 15.5993 16.6551 15.4375 17.1404 15.4375H23.6919C24.0693 15.4375 24.4064 15.5656 24.7029 15.8217C24.9995 16.0778 25.1747 16.3811 25.2287 16.7316L26.2801 22.3934C26.3341 22.8248 26.3206 23.1887 26.2397 23.4853C26.1588 23.7819 26.0105 24.038 25.7948 24.2537L21.872 28.2169C22.4113 29.2145 23.0516 30.1783 23.793 31.1085C24.5344 32.0386 25.35 32.935 26.2397 33.7978C27.0755 34.6336 27.9517 35.4087 28.8684 36.1232C29.785 36.8376 30.7556 37.4914 31.7801 38.0846L35.5816 34.2831C35.8242 34.0404 36.141 33.8585 36.532 33.7371C36.9229 33.6158 37.3071 33.5821 37.6845 33.636L43.2654 34.7684C43.6429 34.8762 43.9529 35.0717 44.1956 35.3548C44.4382 35.6379 44.5595 35.9547 44.5595 36.3051V42.8566C44.5595 43.3419 44.3978 43.7463 44.0742 44.0699C43.7507 44.3934 43.3463 44.5551 42.861 44.5551Z"
        fill="white"
      />
    </g>
  )],
  ["webhook", (
    <g key="webhook">
      <circle
        cx={wheelLogoRadius}
        cy={wheelLogoRadius}
        fill="#14B8A6"
        r={wheelLogoRadius - wheelLogoStrokeWidth}
        stroke="white"
        strokeWidth={wheelLogoStrokeWidth}
      />
      <path
        d="M21.4356 45.4151C19.0661 45.4151 17.0463 44.5801 15.3762 42.91C13.7061 41.2399 12.8711 39.2202 12.8711 36.8507C12.8711 34.7666 13.5206 32.9467 14.8195 31.3908C16.1185 29.8349 17.7528 28.8571 19.7227 28.4575V32.0117C18.7235 32.3543 17.9027 32.9681 17.2604 33.8531C16.6181 34.7381 16.2969 35.7373 16.2969 36.8507C16.2969 38.2781 16.7965 39.4914 17.7957 40.4906C18.7949 41.4897 20.0082 41.9893 21.4356 41.9893C22.863 41.9893 24.0763 41.4897 25.0755 40.4906C26.0747 39.4914 26.5743 38.2781 26.5743 36.8507V35.1378H36.6375C36.8659 34.8808 37.1442 34.6738 37.4726 34.5168C37.8009 34.3598 38.1648 34.2813 38.5645 34.2813C39.2782 34.2813 39.8849 34.5311 40.3845 35.0307C40.8841 35.5303 41.1339 36.1369 41.1339 36.8507C41.1339 37.5644 40.8841 38.171 40.3845 38.6706C39.8849 39.1702 39.2782 39.42 38.5645 39.42C38.1648 39.42 37.8009 39.3415 37.4726 39.1845C37.1442 39.0275 36.8659 38.8205 36.6375 38.5635H29.8288C29.4291 40.5334 28.4513 42.1678 26.8954 43.4667C25.3395 44.7657 23.5196 45.4151 21.4356 45.4151ZM38.5645 45.4151C36.9658 45.4151 35.517 45.0226 34.2181 44.2375C32.9191 43.4524 31.8985 42.4176 31.1562 41.1329H35.7382C36.1379 41.4184 36.5804 41.6325 37.0657 41.7752C37.5511 41.918 38.0507 41.9893 38.5645 41.9893C39.9919 41.9893 41.2052 41.4897 42.2044 40.4906C43.2036 39.4914 43.7032 38.2781 43.7032 36.8507C43.7032 35.4232 43.2036 34.2099 42.2044 33.2107C41.2052 32.2116 39.9919 31.712 38.5645 31.712C37.9936 31.712 37.4654 31.7905 36.9801 31.9475C36.4948 32.1045 36.038 32.34 35.6098 32.6541L30.3854 23.9611C29.7859 23.8469 29.2863 23.5614 28.8867 23.1047C28.487 22.6479 28.2872 22.0912 28.2872 21.4346C28.2872 20.7209 28.5369 20.1142 29.0365 19.6146C29.5361 19.115 30.1428 18.8653 30.8565 18.8653C31.5702 18.8653 32.1768 19.115 32.6764 19.6146C33.176 20.1142 33.4258 20.7209 33.4258 21.4346V21.7986C33.4258 21.8985 33.3973 22.0198 33.3402 22.1626L37.0657 28.4146C37.2941 28.3575 37.5368 28.3219 37.7937 28.3076C38.0507 28.2933 38.3076 28.2862 38.5645 28.2862C40.934 28.2862 42.9538 29.1212 44.6239 30.7913C46.294 32.4614 47.129 34.4811 47.129 36.8507C47.129 39.2202 46.294 41.2399 44.6239 42.91C42.9538 44.5801 40.934 45.4151 38.5645 45.4151ZM21.4356 39.42C20.7219 39.42 20.1152 39.1702 19.6156 38.6706C19.116 38.171 18.8662 37.5644 18.8662 36.8507C18.8662 36.2226 19.0661 35.6802 19.4657 35.2234C19.8654 34.7666 20.3507 34.4669 20.9217 34.3241L24.947 27.6438C24.1191 26.873 23.4696 25.9524 22.9986 24.8818C22.5275 23.8112 22.292 22.6622 22.292 21.4346C22.292 19.0651 23.1271 17.0453 24.7971 15.3752C26.4672 13.7052 28.487 12.8701 30.8565 12.8701C33.226 12.8701 35.2458 13.7052 36.9159 15.3752C38.5859 17.0453 39.421 19.0651 39.421 21.4346H35.9952C35.9952 20.0072 35.4956 18.7939 34.4964 17.7947C33.4972 16.7955 32.2839 16.2959 30.8565 16.2959C29.4291 16.2959 28.2158 16.7955 27.2166 17.7947C26.2174 18.7939 25.7178 20.0072 25.7178 21.4346C25.7178 22.6622 26.0889 23.7399 26.8312 24.6677C27.5734 25.5955 28.5155 26.1879 29.6575 26.4448L23.8764 36.0798C23.9335 36.2226 23.9692 36.3511 23.9835 36.4653C23.9978 36.5794 24.0049 36.7079 24.0049 36.8507C24.0049 37.5644 23.7551 38.171 23.2555 38.6706C22.7559 39.1702 22.1493 39.42 21.4356 39.42Z"
        fill="white"
      />
    </g>
  )],
  ["pagerduty", (
    <g key="pagerduty">
      <circle
        cx={wheelLogoRadius}
        cy={wheelLogoRadius}
        fill="#06AC38"
        r={wheelLogoRadius - wheelLogoStrokeWidth}
        stroke="white"
        strokeWidth={wheelLogoStrokeWidth}
      />

      <path
        d="M25.689 36.9179V45.8337H21.1763V36.9179H25.689ZM30.1967 12.3516C34.0634 12.3563 35.7394 12.5953 38.121 13.997C40.7373 15.5276 42.3527 18.5697 42.3527 22.358C42.3527 25.8784 41.0358 28.9205 38.5073 30.7381C36.1894 32.4217 33.5556 32.6513 30.0437 32.6513H21.1763V12.3516L30.1967 12.3516ZM30.7812 16.6564L25.689 16.6947V28.4039H31.0797C34.8549 28.4039 37.7347 26.7585 37.7347 22.4154C37.7347 18.3784 35.4168 16.6181 30.7812 16.6564Z"
        fill="white"
      />
    </g>
  )],
  ["slack", (
    <g key="slack">
      <circle
        cx={wheelLogoRadius}
        cy={wheelLogoRadius}
        fill="#510F4E"
        r={wheelLogoRadius - wheelLogoStrokeWidth}
        stroke="white"
        strokeWidth={wheelLogoStrokeWidth}
      />

      <path
        clipRule="evenodd"
        d="M25.293 12.3525C23.3419 12.354 21.763 13.9341 21.7644 15.8812C21.763 17.8283 23.3434 19.4085 25.2944 19.4099H28.8244V15.8827C28.8258 13.9356 27.2454 12.3554 25.293 12.3525C25.2944 12.3525 25.2944 12.3525 25.293 12.3525ZM25.293 21.7643H15.8825C13.9315 21.7657 12.3511 23.3459 12.3525 25.293C12.3497 27.2401 13.9301 28.8202 15.8811 28.8231H25.293C27.244 28.8217 28.8244 27.2415 28.8229 25.2944C28.8244 23.3459 27.244 21.7657 25.293 21.7643Z"
        fill="#36C5F0"
        fillRule="evenodd"
      />

      <path
        clipRule="evenodd"
        d="M47.6467 25.293C47.6481 23.3459 46.0677 21.7657 44.1167 21.7643C42.1657 21.7657 40.5852 23.3459 40.5867 25.293V28.8231H44.1167C46.0677 28.8217 47.6481 27.2415 47.6467 25.293ZM38.2348 25.293V15.8812C38.2363 13.9356 36.6573 12.3554 34.7063 12.3525C32.7553 12.354 31.1748 13.9341 31.1763 15.8812V25.293C31.1734 27.2401 32.7538 28.8202 34.7048 28.8231C36.6558 28.8217 38.2363 27.2415 38.2348 25.293Z"
        fill="#2EB67D"
        fillRule="evenodd"
      />

      <path
        clipRule="evenodd"
        d="M34.7048 47.6459C36.6558 47.6444 38.2362 46.0643 38.2348 44.1172C38.2362 42.17 36.6558 40.5899 34.7048 40.5885H31.1748V44.1172C31.1734 46.0628 32.7538 47.643 34.7048 47.6459ZM34.7048 38.2327H44.1167C46.0677 38.2312 47.6481 36.6511 47.6466 34.704C47.6495 32.7568 46.0691 31.1767 44.1181 31.1738H34.7062C32.7552 31.1753 31.1748 32.7554 31.1762 34.7025C31.1748 36.6511 32.7538 38.2312 34.7048 38.2327Z"
        fill="#ECB22E"
        fillRule="evenodd"
      />

      <path
        clipRule="evenodd"
        d="M12.353 34.7045C12.3516 36.6516 13.932 38.2317 15.883 38.2332C17.834 38.2317 19.4144 36.6516 19.413 34.7045V31.1758H15.883C13.932 31.1772 12.3516 32.7574 12.353 34.7045ZM21.7649 34.7045V44.1162C21.762 46.0633 23.3424 47.6435 25.2934 47.6464C27.2444 47.6449 28.8249 46.0648 28.8234 44.1177V34.7074C28.8263 32.7602 27.2459 31.1801 25.2949 31.1772C23.3424 31.1772 21.7634 32.7574 21.7649 34.7045Z"
        fill="#E01E5A"
        fillRule="evenodd"
      />
    </g>
  )],
  ["email", (
    <g key="email">
      <circle
        cx={wheelLogoRadius}
        cy={wheelLogoRadius}
        fill="#0891B2"
        r={wheelLogoRadius - wheelLogoStrokeWidth}
        stroke="white"
        strokeWidth={wheelLogoStrokeWidth}
      />

      <path
        d="M17.0585 42.939C16.1688 42.939 15.4072 42.6222 14.7736 41.9886C14.14 41.355 13.8232 40.5934 13.8232 39.7037V20.2919C13.8232 19.4022 14.14 18.6406 14.7736 18.007C15.4072 17.3734 16.1688 17.0566 17.0585 17.0566H42.9409C43.8306 17.0566 44.5922 17.3734 45.2258 18.007C45.8594 18.6406 46.1762 19.4022 46.1762 20.2919V39.7037C46.1762 40.5934 45.8594 41.355 45.2258 41.9886C44.5922 42.6222 43.8306 42.939 42.9409 42.939H17.0585ZM29.9997 31.6155L42.9409 23.5272V20.2919L29.9997 28.3802L17.0585 20.2919V23.5272L29.9997 31.6155Z"
        fill="white"
      />
    </g>
  )],
];

const logoCount = logos.length;

export default function IntegrationsWheel({ ...props }: ImageProps) {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const svg = svgRef.current;

    if (svg) {
      const w = svg.width.baseVal.value;
      const h = svg.height.baseVal.value;

      svg.childNodes.forEach((child) => {
        if (child instanceof SVGSVGElement) {
          child.setAttribute("x", `${(w - child.width.baseVal.value) / 2}`);
          child.setAttribute("y", `${(h - child.height.baseVal.value) / 2}`);
        }
      });

      svg.setAttribute("width", "100%");
      svg.setAttribute("height", "100%");
    }
  }, [svgRef]);

  return (
    <svg
      {...props}
      fill="none"
      viewBox={viewBox}
      xmlns="http://www.w3.org/2000/svg"
    >
      <svg ref={svgRef}>
        <WebGazerLogo />
      </svg>

      <g id="outer">
        <AnimateTransform from={`0 ${wheelWidth / 2} ${wheelHeight / 2}`} to={`360 ${wheelWidth / 2} ${wheelHeight / 2}`} />

        <circle
          className={styles.wheelBaseStrokeColor}
          cx={circleCenter.x}
          cy={circleCenter.y}
          r={circleRadius}
          strokeWidth="1.5"
        />

        {logos.map(([key, logo], index) => {
          return (
            <g key={key}>
              <circle
                className={styles.wheelBaseFillColor}
                cx={circleRadius * Math.cos(index * Math.PI / logoCount * 2 + Math.PI / logoCount) + circleCenter.x}
                cy={circleRadius * Math.sin(index * Math.PI / logoCount * 2 + Math.PI / logoCount) + circleCenter.y}
                r={wheelDotRadius}
              >
              </circle>

              <svg
                fill="none"
                height={wheelLogoRadius * 2}
                id="key"
                key="chat"
                viewBox={`0 0 ${wheelLogoRadius * 2} ${wheelLogoRadius * 2}`}
                width={wheelLogoRadius * 2}
                x={Math.round(circleRadius * Math.cos(index * Math.PI / logoCount * 2) + circleCenter.x - wheelLogoRadius)}
                xmlns="http://www.w3.org/2000/svg"
                y={Math.round(circleRadius * Math.sin(index * Math.PI / logoCount * 2) + circleCenter.y - wheelLogoRadius)}
              >
                {isValidElement(logo) && cloneElement(logo as ReactElement<PropsWithoutRef<JSX.IntrinsicElements["g"]>>, {
                  style: {
                    transformOrigin: "50% 50%",
                  },
                }, [...React.Children.map(logo, (child) => {
                  if (isValidElement(child)) {
                    return cloneElement(child);
                  }
                  return child;
                }), (
                  <AnimateTransform
                    from="360"
                    key={`${key}-rotate`}
                    to="0"
                    type="rotate"
                  />
                )])}
              </svg>
            </g>
          );
        })}
      </g>
    </svg>
  );
};
